import { useEffect, useState } from 'react';
import { Link, Navigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { http } from '@/lib/api';
import { toast } from 'sonner';
import {
  ArrowLeft, Sparkles, TrendingUp, Users, MessageCircle, BookOpen, Activity,
  ChevronRight, RefreshCw, MessageSquare, Vote, ClipboardList, Heart,
} from 'lucide-react';

const TABS = [
  { id: 'engagement', label: 'Engagement', icon: TrendingUp },
  { id: 'retention', label: 'Retention', icon: Activity },
  { id: 'ai', label: 'AI Usage', icon: MessageCircle },
  { id: 'learning', label: 'Learning', icon: BookOpen },
  { id: 'progress', label: 'Progress', icon: Sparkles },
  { id: 'funnel', label: 'Funnel', icon: ChevronRight },
  { id: 'heatmap', label: 'Heatmap', icon: Users },
  { id: 'feedback', label: 'Feedback', icon: MessageSquare },
  { id: 'surveys', label: 'Surveys', icon: ClipboardList },
  { id: 'features', label: 'Feature Votes', icon: Vote },
  { id: 'insights', label: 'AI Insights', icon: Heart },
];

const Metric = ({ label, value, sub }) => (
  <div className="bg-white rounded-2xl p-5 card-soft">
    <p className="text-xs uppercase tracking-widest text-navy/50 font-semibold">{label}</p>
    <p className="font-heading text-3xl lg:text-4xl mt-1 font-light">{value ?? '—'}</p>
    {sub && <p className="text-xs text-navy/50 mt-1">{sub}</p>}
  </div>
);

const Bar = ({ label, count, max, tone = 'bg-gold' }) => (
  <div className="flex items-center gap-3">
    <span className="text-xs w-40 truncate text-navy/70">{label}</span>
    <div className="flex-1 h-2 bg-beige rounded-full overflow-hidden">
      <div className={`h-full ${tone} transition-all`} style={{ width: `${Math.max(2, Math.min(100, (count / Math.max(1, max)) * 100))}%` }}/>
    </div>
    <span className="text-xs text-navy/50 w-10 text-right tabular-nums">{count}</span>
  </div>
);

export default function AdminAnalytics() {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const initialTab = searchParams.get('tab') || 'engagement';
  const [tab, setTab] = useState(initialTab);
  const [core, setCore] = useState(null);
  const [funnel, setFunnel] = useState(null);
  const [heatmap, setHeatmap] = useState(null);
  const [feedback, setFeedback] = useState(null);
  const [surveys, setSurveys] = useState(null);
  const [featVotes, setFeatVotes] = useState(null);
  const [insights, setInsights] = useState(null);
  const [insightsLoading, setInsightsLoading] = useState(false);

  useEffect(() => {
    if (!user?.is_admin) return;
    http.get('/admin/analytics').then(r => setCore(r.data)).catch(() => {});
    http.get('/admin/funnel').then(r => setFunnel(r.data)).catch(() => {});
    http.get('/admin/heatmap').then(r => setHeatmap(r.data)).catch(() => {});
    http.get('/admin/feedback').then(r => setFeedback(r.data)).catch(() => {});
    http.get('/admin/surveys').then(r => setSurveys(r.data)).catch(() => {});
    http.get('/admin/feature-votes').then(r => setFeatVotes(r.data)).catch(() => {});
    http.get('/admin/insights').then(r => setInsights(r.data)).catch(() => {});
  }, [user]);

  if (!user?.is_admin) return <Navigate to="/app" replace />;

  const regenerateInsights = async () => {
    setInsightsLoading(true);
    try {
      const { data } = await http.get('/admin/insights?refresh=true');
      setInsights(data);
      toast.success('Insights refreshed');
    } catch {
      toast.error('Could not regenerate — Emergent LLM key may be rate-limited');
    } finally { setInsightsLoading(false); }
  };

  return (
    <div className="p-6 lg:px-10 lg:pt-10 space-y-6 lg:space-y-8" data-testid="admin-analytics-page">
      <Link to="/app/admin" data-testid="admin-analytics-back" className="inline-flex items-center gap-2 text-sm text-navy/60 hover:text-navy">
        <ArrowLeft size={14}/> Back to admin
      </Link>
      <div>
        <p className="text-xs uppercase tracking-[0.2em] text-gold font-semibold">Analytics</p>
        <h1 className="font-heading text-3xl lg:text-5xl mt-1 lg:mt-2">How Brotherhood+ is being used.</h1>
        <p className="hidden lg:block text-navy/60 mt-3 max-w-3xl">Anonymized aggregates. No private journals, chats, or assessment answers. Refresh the page to recompute — all metrics are live.</p>
      </div>

      {/* Tabs */}
      <div className="flex flex-wrap gap-2 border-b border-navy/5 pb-4">
        {TABS.map(t => (
          <button
            key={t.id}
            data-testid={`admin-tab-${t.id}`}
            onClick={() => setTab(t.id)}
            className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-smooth ${
              tab === t.id ? 'bg-navy text-white' : 'text-navy/70 bg-beige hover:bg-[#EAE5D9]'
            }`}
          >
            <t.icon size={13}/>{t.label}
          </button>
        ))}
      </div>

      {/* ENGAGEMENT */}
      {tab === 'engagement' && core?.engagement && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 lg:gap-4" data-testid="tab-engagement">
          <Metric label="Total users" value={core.engagement.total_users}/>
          <Metric label="DAU" value={core.engagement.dau} sub="Active in last 24h"/>
          <Metric label="WAU" value={core.engagement.wau} sub="Active in last 7 days"/>
          <Metric label="MAU" value={core.engagement.mau} sub="Active in last 30 days"/>
          <Metric label="New users (7d)" value={core.engagement.new_users_7d}/>
          <Metric label="New users (30d)" value={core.engagement.new_users_30d}/>
          <Metric label="Returning (7d)" value={core.engagement.returning_users_7d} sub="Signed up 8+ days ago"/>
          <Metric label="Sessions (7d)" value={core.engagement.sessions_last_7d}/>
          <Metric label="Avg session" value={`${core.engagement.avg_session_min}m`} sub="Duration per session"/>
          <Metric label="Sessions/user/wk" value={core.engagement.sessions_per_week_per_user}/>
        </div>
      )}

      {/* RETENTION */}
      {tab === 'retention' && core?.retention && (
        <div className="space-y-5" data-testid="tab-retention">
          <div className="grid grid-cols-3 gap-3 lg:gap-4">
            <Metric label="Day 1" value={`${core.retention.d1_retention_pct}%`} sub={`of ${core.retention.d1_cohort_size} users`}/>
            <Metric label="Day 7" value={`${core.retention.d7_retention_pct}%`} sub={`of ${core.retention.d7_cohort_size} users`}/>
            <Metric label="Day 30" value={`${core.retention.d30_retention_pct}%`} sub={`of ${core.retention.d30_cohort_size} users`}/>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 lg:gap-4">
            <Metric label="Lesson completion" value={`${core.retention.lesson_completion_rate_pct}%`} sub="Users who did ≥1 lesson"/>
            <Metric label="Growth plan completion" value={`${core.retention.growth_plan_completion_rate_pct}%`} sub="Finished a full plan"/>
            <Metric label="Check-in completion" value={`${core.retention.checkin_completion_rate_pct}%`} sub="Users with any check-in"/>
          </div>
        </div>
      )}

      {/* AI USAGE */}
      {tab === 'ai' && core?.ai && (
        <div className="space-y-5" data-testid="tab-ai">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 lg:gap-4">
            <Metric label="Coach messages" value={core.ai.total_coach_messages}/>
            <Metric label="User messages" value={core.ai.total_user_messages}/>
            <Metric label="Coach sessions" value={core.ai.total_coach_sessions}/>
            <Metric label="Avg msgs/session" value={core.ai.avg_messages_per_session}/>
            <Metric label="Support Coach msgs" value={core.ai.support_coach_messages}/>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-6">
            <div className="bg-white rounded-3xl p-6 card-soft">
              <p className="font-heading text-lg mb-3">Top topics</p>
              <div className="space-y-2">
                {(core.ai.top_topics || []).map(t => (
                  <Bar key={t.topic} label={t.topic} count={t.count} max={core.ai.top_topics[0]?.count || 1}/>
                ))}
                {(!core.ai.top_topics || core.ai.top_topics.length === 0) && <p className="text-xs text-navy/50">No topic data yet.</p>}
              </div>
            </div>
            <div className="bg-white rounded-3xl p-6 card-soft">
              <p className="font-heading text-lg mb-3">Common emotions</p>
              <div className="space-y-2">
                {(core.ai.top_emotions || []).map(e => (
                  <Bar key={e.emotion} label={e.emotion} count={e.count} max={core.ai.top_emotions[0]?.count || 1} tone="bg-navy"/>
                ))}
                {(!core.ai.top_emotions || core.ai.top_emotions.length === 0) && <p className="text-xs text-navy/50">No emotion data yet.</p>}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* LEARNING */}
      {tab === 'learning' && core?.learning && (
        <div className="space-y-5" data-testid="tab-learning">
          <Metric label="Avg lesson completion time" value={core.learning.avg_lesson_completion_minutes ? `${core.learning.avg_lesson_completion_minutes} min` : '—'}/>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-6">
            <div className="bg-white rounded-3xl p-6 card-soft">
              <p className="font-heading text-lg mb-3">Most completed lessons</p>
              <div className="space-y-2">
                {(core.learning.top_completed_lessons || []).map(l => (
                  <Bar key={l.lesson_id} label={l.lesson_id} count={l.count} max={core.learning.top_completed_lessons[0]?.count || 1}/>
                ))}
                {(!core.learning.top_completed_lessons || core.learning.top_completed_lessons.length === 0) && <p className="text-xs text-navy/50">No completions yet.</p>}
              </div>
            </div>
            <div className="bg-white rounded-3xl p-6 card-soft">
              <p className="font-heading text-lg mb-3">Highest abandonment</p>
              <div className="space-y-2">
                {(core.learning.abandoned_lessons || []).map(l => (
                  <div key={l.lesson_id} className="flex items-center gap-3">
                    <span className="text-xs w-40 truncate text-navy/70">{l.lesson_id}</span>
                    <div className="flex-1 h-2 bg-beige rounded-full overflow-hidden">
                      <div className="h-full bg-red-400 transition-all" style={{ width: `${Math.min(100, l.abandon_pct)}%` }}/>
                    </div>
                    <span className="text-xs text-red-700 w-16 text-right tabular-nums">{l.abandon_pct}%</span>
                  </div>
                ))}
                {(!core.learning.abandoned_lessons || core.learning.abandoned_lessons.length === 0) && <p className="text-xs text-navy/50">No abandonment tracked yet.</p>}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* PROGRESS */}
      {tab === 'progress' && core?.progress && (
        <div className="space-y-5" data-testid="tab-progress">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 lg:gap-4">
            <Metric label="Max streak" value={core.progress.max_streak}/>
            <Metric label="Users w/ streak" value={core.progress.users_with_active_streak}/>
            <Metric label="Attachment done" value={core.progress.attachment_assessments_completed}/>
            <Metric label="Weekly review ready" value={core.progress.users_eligible_weekly_review}/>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-6">
            <div className="bg-white rounded-3xl p-6 card-soft">
              <p className="font-heading text-lg mb-3">Mood distribution</p>
              <div className="space-y-2">
                {(core.progress.mood_distribution || []).map(m => (
                  <Bar key={m.mood} label={m.mood} count={m.count} max={core.progress.mood_distribution[0]?.count || 1} tone="bg-gold"/>
                ))}
                {(!core.progress.mood_distribution || core.progress.mood_distribution.length === 0) && <p className="text-xs text-navy/50">No check-ins yet.</p>}
              </div>
            </div>
            <div className="bg-white rounded-3xl p-6 card-soft">
              <p className="font-heading text-lg mb-3">Wellness score by month</p>
              <div className="space-y-2">
                {(core.progress.wellness_trend_by_month || []).map(m => (
                  <Bar key={m.month} label={`${m.month} (${m.count})`} count={m.avg} max={100} tone="bg-navy"/>
                ))}
                {(!core.progress.wellness_trend_by_month || core.progress.wellness_trend_by_month.length === 0) && <p className="text-xs text-navy/50">No assessments yet.</p>}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* FUNNEL */}
      {tab === 'funnel' && funnel && (
        <div className="bg-white rounded-3xl p-6 lg:p-8 card-soft" data-testid="tab-funnel">
          <p className="font-heading text-xl lg:text-2xl mb-4">Conversion funnel</p>
          <div className="space-y-3">
            {funnel.stages.map((s, i) => {
              const first = funnel.stages[0]?.count || 1;
              const barPct = Math.max(3, Math.min(100, (s.count / first) * 100));
              return (
                <div key={s.stage} data-testid={`funnel-stage-${i}`} className="grid grid-cols-12 items-center gap-3">
                  <div className="col-span-4 lg:col-span-3">
                    <p className="text-sm font-medium text-navy">{s.stage}</p>
                  </div>
                  <div className="col-span-6 lg:col-span-7 h-6 bg-beige rounded-full overflow-hidden">
                    <div className="h-full bg-gradient-to-r from-navy to-gold transition-all duration-700" style={{ width: `${barPct}%` }}/>
                  </div>
                  <div className="col-span-2 text-right">
                    <p className="text-sm font-medium tabular-nums">{s.count}</p>
                    {s.conversion_from_prev_pct !== null && s.conversion_from_prev_pct !== undefined && (
                      <p className="text-[10px] text-navy/50 tabular-nums">{s.conversion_from_prev_pct}%↓</p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
          <p className="text-xs text-navy/40 mt-4">Landing visits are anonymous (from analytics events); every stage after account creation is authenticated.</p>
        </div>
      )}

      {/* HEATMAP */}
      {tab === 'heatmap' && heatmap && (
        <div className="space-y-5" data-testid="tab-heatmap">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-6">
            <div className="bg-white rounded-3xl p-6 card-soft">
              <p className="font-heading text-lg mb-3">Most clicked elements</p>
              <div className="space-y-2">
                {(heatmap.top_clicks || []).slice(0, 15).map(c => (
                  <Bar key={c.element} label={c.element} count={c.count} max={heatmap.top_clicks[0]?.count || 1}/>
                ))}
                {(!heatmap.top_clicks || heatmap.top_clicks.length === 0) && <p className="text-xs text-navy/50">No clicks tracked yet.</p>}
              </div>
            </div>
            <div className="bg-white rounded-3xl p-6 card-soft">
              <p className="font-heading text-lg mb-3">Rage clicks (frustration)</p>
              <div className="space-y-2">
                {(heatmap.rage_clicks || []).map((r, i) => (
                  <Bar key={i} label={`${r.page} · ${r.element}`} count={r.count} max={(heatmap.rage_clicks[0]?.count) || 1} tone="bg-red-400"/>
                ))}
                {(!heatmap.rage_clicks || heatmap.rage_clicks.length === 0) && <p className="text-xs text-navy/50">No rage clicks — great UX!</p>}
              </div>
            </div>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-6">
            <div className="bg-white rounded-3xl p-6 card-soft">
              <p className="font-heading text-lg mb-3">Longest time on page</p>
              <div className="space-y-2">
                {(heatmap.top_time_on_page || []).map(p => (
                  <div key={p.page} className="flex items-center gap-3">
                    <span className="text-xs w-40 truncate text-navy/70">{p.page}</span>
                    <div className="flex-1 h-2 bg-beige rounded-full overflow-hidden">
                      <div className="h-full bg-navy" style={{ width: `${Math.min(100, (p.avg_seconds / (heatmap.top_time_on_page[0]?.avg_seconds || 1)) * 100)}%` }}/>
                    </div>
                    <span className="text-xs text-navy/50 w-14 text-right tabular-nums">{p.avg_seconds}s</span>
                  </div>
                ))}
                {(!heatmap.top_time_on_page || heatmap.top_time_on_page.length === 0) && <p className="text-xs text-navy/50">No time data yet.</p>}
              </div>
            </div>
            <div className="bg-white rounded-3xl p-6 card-soft">
              <p className="font-heading text-lg mb-3">Quick-leave pages</p>
              <div className="space-y-2">
                {(heatmap.quick_leave_pages || []).map(p => (
                  <div key={p.page} className="flex items-center gap-3">
                    <span className="text-xs w-40 truncate text-navy/70">{p.page}</span>
                    <span className="text-xs text-red-700 tabular-nums ml-auto">{p.avg_seconds}s · {p.visits} visits</span>
                  </div>
                ))}
                {(!heatmap.quick_leave_pages || heatmap.quick_leave_pages.length === 0) && <p className="text-xs text-navy/50">No bounce-heavy pages.</p>}
              </div>
            </div>
          </div>
          <div className="bg-white rounded-3xl p-6 card-soft">
            <p className="font-heading text-lg mb-3">Scroll depth (by page)</p>
            <div className="space-y-2">
              {(heatmap.scroll_depth_by_page || []).map(p => (
                <div key={p.page} className="flex items-center gap-3">
                  <span className="text-xs w-48 truncate text-navy/70">{p.page}</span>
                  <div className="flex-1 h-2 bg-beige rounded-full overflow-hidden">
                    <div className="h-full bg-gold" style={{ width: `${Math.min(100, p.avg_depth_pct)}%` }}/>
                  </div>
                  <span className="text-xs text-navy/50 w-16 text-right tabular-nums">{p.avg_depth_pct}%</span>
                </div>
              ))}
              {(!heatmap.scroll_depth_by_page || heatmap.scroll_depth_by_page.length === 0) && <p className="text-xs text-navy/50">No scroll data yet.</p>}
            </div>
          </div>
        </div>
      )}

      {/* FEEDBACK */}
      {tab === 'feedback' && feedback && (
        <div className="space-y-5" data-testid="tab-feedback">
          <div className="flex flex-wrap gap-2">
            {(feedback.by_category || []).map(c => (
              <span key={c.category} className="inline-flex items-center gap-2 bg-beige text-navy rounded-full px-3 py-1.5 text-xs">
                {c.category} <span className="text-navy/50">· {c.count}</span>
              </span>
            ))}
            <span className="ml-auto text-sm text-navy/50">Total: {feedback.total}</span>
          </div>
          <div className="space-y-3">
            {(feedback.items || []).map(f => (
              <div key={f.id} data-testid={`feedback-item-${f.id}`} className="bg-white rounded-2xl p-5 card-soft">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-center gap-3">
                    <span className={`text-[10px] uppercase tracking-widest rounded-full px-2.5 py-1 font-semibold ${
                      f.category === 'bug' ? 'bg-red-50 text-red-700' :
                      f.category === 'feature' ? 'bg-gold/10 text-gold' :
                      f.category === 'confusing' ? 'bg-beige text-navy' :
                      f.category === 'love' ? 'bg-green/10 text-green' : 'bg-beige text-navy'
                    }`}>{f.category}</span>
                    <p className="text-xs text-navy/60">{f.user_email}</p>
                  </div>
                  <p className="text-[10px] text-navy/40">{new Date(f.created_at).toLocaleString()}</p>
                </div>
                <p className="text-sm text-navy/85 mt-2 leading-relaxed whitespace-pre-wrap">{f.message}</p>
                <p className="text-[10px] text-navy/40 mt-2">
                  {f.page_url && <>Page: {f.page_url} · </>}
                  {f.user_agent && <>{f.user_agent.slice(0, 80)}</>}
                </p>
              </div>
            ))}
            {(!feedback.items || feedback.items.length === 0) && <p className="text-xs text-navy/50">No feedback yet.</p>}
          </div>
        </div>
      )}

      {/* SURVEYS */}
      {tab === 'surveys' && surveys && (
        <div className="space-y-5" data-testid="tab-surveys">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 lg:gap-4">
            <Metric label="NPS score" value={surveys.nps} sub={`${surveys.count} responses`}/>
            <Metric label="Promoters (9-10)" value={surveys.promoters}/>
            <Metric label="Passives (7-8)" value={surveys.passives}/>
            <Metric label="Detractors (0-6)" value={surveys.detractors}/>
          </div>
          <div className="bg-white rounded-3xl p-6 card-soft">
            <p className="font-heading text-lg mb-3">Score distribution</p>
            <div className="grid grid-cols-11 gap-1 items-end h-24">
              {(surveys.distribution || []).map(d => (
                <div key={d.score} className="flex flex-col items-center gap-1 h-full justify-end">
                  <div className={`w-full rounded-t-md transition-all ${d.score <= 6 ? 'bg-red-300' : d.score <= 8 ? 'bg-beige border border-navy/10' : 'bg-green/70'}`}
                    style={{ height: `${Math.max(4, (d.count / Math.max(1, ...surveys.distribution.map(x => x.count))) * 100)}%` }}/>
                  <span className="text-[10px] text-navy/50">{d.score}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="space-y-3">
            {(surveys.items || []).map(s => (
              <div key={s.id} className="bg-white rounded-2xl p-5 card-soft" data-testid={`survey-item-${s.id}`}>
                <div className="flex items-center justify-between">
                  <span className={`text-2xl font-heading font-light ${
                    s.nps_score >= 9 ? 'text-green' : s.nps_score >= 7 ? 'text-navy' : 'text-red-700'
                  }`}>{s.nps_score}</span>
                  <p className="text-xs text-navy/40">{s.user_email} · {new Date(s.created_at).toLocaleDateString()}</p>
                </div>
                {s.most_helped && <p className="text-sm text-navy/80 mt-2"><b className="text-navy/50 uppercase text-[10px] tracking-widest">What helped:</b> {s.most_helped}</p>}
                {s.almost_stopped && <p className="text-sm text-navy/80 mt-1"><b className="text-navy/50 uppercase text-[10px] tracking-widest">Almost stopped:</b> {s.almost_stopped}</p>}
                {s.would_make_better && <p className="text-sm text-navy/80 mt-1"><b className="text-navy/50 uppercase text-[10px] tracking-widest">Improvement:</b> {s.would_make_better}</p>}
              </div>
            ))}
            {(!surveys.items || surveys.items.length === 0) && <p className="text-xs text-navy/50">No survey responses yet.</p>}
          </div>
        </div>
      )}

      {/* FEATURE VOTES */}
      {tab === 'features' && featVotes && (
        <div className="space-y-5" data-testid="tab-features">
          <div className="grid grid-cols-2 gap-3 lg:gap-4">
            <Metric label="Total votes" value={featVotes.total_votes}/>
            <Metric label="Unique voters" value={featVotes.unique_voters}/>
          </div>
          <div className="bg-white rounded-3xl p-6 card-soft">
            <div className="space-y-3">
              {(featVotes.features || []).map(f => (
                <div key={f.id} className="flex items-center gap-4">
                  <span className="text-2xl">{f.emoji}</span>
                  <div className="flex-1">
                    <p className="font-heading text-base">{f.title}</p>
                    <p className="text-xs text-navy/50">{f.category}</p>
                  </div>
                  <div className="w-40 h-2 bg-beige rounded-full overflow-hidden">
                    <div className="h-full bg-gold" style={{ width: `${(f.votes / Math.max(1, featVotes.features[0]?.votes || 1)) * 100}%` }}/>
                  </div>
                  <span className="text-sm font-medium w-8 text-right tabular-nums">{f.votes}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* AI INSIGHTS */}
      {tab === 'insights' && (
        <div className="bg-white rounded-3xl p-6 lg:p-10 card-soft" data-testid="tab-insights">
          <div className="flex items-start justify-between gap-3 mb-4">
            <div>
              <p className="text-xs uppercase tracking-[0.2em] text-gold font-semibold inline-flex items-center gap-1"><Sparkles size={11}/>AI Insights</p>
              <p className="font-heading text-2xl lg:text-3xl mt-1">Weekly executive summary</p>
              {insights?.created_at && <p className="text-xs text-navy/50 mt-1">Generated {new Date(insights.created_at).toLocaleString()}{insights.cached ? ' · cached' : ''}</p>}
            </div>
            <button data-testid="insights-refresh" onClick={regenerateInsights} disabled={insightsLoading}
              className="inline-flex items-center gap-2 bg-beige text-navy rounded-full px-4 py-2 text-sm hover:bg-[#EAE5D9] disabled:opacity-50">
              <RefreshCw size={13} className={insightsLoading ? 'animate-spin' : ''}/>
              {insightsLoading ? 'Generating…' : 'Regenerate'}
            </button>
          </div>
          {insights?.summary ? (
            <div className="prose prose-sm lg:prose-base max-w-none whitespace-pre-wrap text-navy/85 leading-relaxed">
              {insights.summary}
            </div>
          ) : (
            <p className="text-sm text-navy/60">Generating first insight… try Regenerate if this stays blank.</p>
          )}
        </div>
      )}
    </div>
  );
}
